import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTable, IDataClassConfig } from 'app/classes/data-table/data-table';
import { Site } from 'app/classes/site';
import { AssetService, EneryAssetCategoryType, IGetEnergyAsset } from 'app/shared/asset.service';
import { SiteService } from 'app/shared/site.service';

@Component({
  selector: 'app-energy-list',
  templateUrl: './energy-list.component.html',
  styleUrl: './energy-list.component.css',
  standalone: false
})
export class EnergyListComponent implements OnInit, OnDestroy {
  assets = signal<IGetEnergyAsset[]>(null);
  filteredAssets = signal<IGetEnergyAsset[]>(null);
  meterRef = signal<string>(null);
  sites = signal<SiteWithTotals[]>([]);
  assetTypesAllowed = [AssetService.ASSET_TYPE_ELEC_METER, AssetService.ASSET_TYPE_GAS_METER];
  months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  grandTotals: number[];
  siteGrandTotals: number[];

  dataTableSites: DataTable;
  dataTableSitesConfig: IDataClassConfig = {
    "title": { "valueType": "string", "title": "Site", "align": "left" },
    "addressOneLine": { "valueType": "string", "title": "Address", "align": "left" },
    "month_jan": { "valueType": "number", "title": "Jan", "align": "right" },
    "month_feb": { "valueType": "number", "title": "Feb", "align": "right" },
    "month_mar": { "valueType": "number", "title": "Mar", "align": "right" },
    "month_apr": { "valueType": "number", "title": "Apr", "align": "right" },
    "month_may": { "valueType": "number", "title": "May", "align": "right" },
    "month_jun": { "valueType": "number", "title": "Jun", "align": "right" },
    "month_jul": { "valueType": "number", "title": "Jul", "align": "right" },
    "month_aug": { "valueType": "number", "title": "Aug", "align": "right" },
    "month_sep": { "valueType": "number", "title": "Sep", "align": "right" },
    "month_oct": { "valueType": "number", "title": "Oct", "align": "right" },
    "month_nov": { "valueType": "number", "title": "Nov", "align": "right" },
    "month_dec": { "valueType": "number", "title": "Dec", "align": "right" },
    "total": { "valueType": "number", "title": "Total", "align": "right" }
  };

  dataTableAssets = signal<DataTable>(null);
  dataTableAssetsConfig: IDataClassConfig = {
    "title": { "valueType": "string", "title": "Asset", "align": "left" },
    "site_title": { "valueType": "string", "title": "Site", "align": "left" },
    "month_jan": { "valueType": "number", "title": "Jan", "align": "right" },
    "month_feb": { "valueType": "number", "title": "Feb", "align": "right" },
    "month_mar": { "valueType": "number", "title": "Mar", "align": "right" },
    "month_apr": { "valueType": "number", "title": "Apr", "align": "right" },
    "month_may": { "valueType": "number", "title": "May", "align": "right" },
    "month_jun": { "valueType": "number", "title": "Jun", "align": "right" },
    "month_jul": { "valueType": "number", "title": "Jul", "align": "right" },
    "month_aug": { "valueType": "number", "title": "Aug", "align": "right" },
    "month_sep": { "valueType": "number", "title": "Sep", "align": "right" },
    "month_oct": { "valueType": "number", "title": "Oct", "align": "right" },
    "month_nov": { "valueType": "number", "title": "Nov", "align": "right" },
    "month_dec": { "valueType": "number", "title": "Dec", "align": "right" },
    "total": { "valueType": "number", "title": "Total", "align": "right" }
  };

  tabIndex = signal<number>(0);
  asset = signal<IGetEnergyAsset>(null);
  currentYear = new Date().getFullYear();
  onYear = signal<number>(null);
  electricToggle = signal<boolean>(true);
  gasToggle = signal<boolean>(true);

  constructor(private assetService: AssetService, private siteService: SiteService, private route: Router, private activatedRoute: ActivatedRoute) {
    this.localStorageGet();
  }

  ngOnInit(): void {
    this.siteService.getSitesContainsAssetTypes(this.assetTypesAllowed).then(sites => {
      this.sites.set(sites.map(site => new SiteWithTotals(site)));
      this.getAssets();
    });
  }

  toggle(type: EneryAssetCategoryType) {
    const previousElec = this.electricToggle();
    const previousGas = this.gasToggle();
    if (type === 'electric') {
      this.electricToggle.set(!this.electricToggle());
    } else if (type === 'gas') {
      this.gasToggle.set(!this.gasToggle());
    }

    if (!this.electricToggle() && !this.gasToggle()) {
      if (previousElec) {
        this.gasToggle.set(true);
      } else {
        this.electricToggle.set(true);
      }
    }

    this.filter();
  }

  dataTableSiteClick(site: SiteWithTotals) {
    console.log(site);
    this.route.navigate(['/energy/site', site.id]);
  }

  dataTableAssetClick(asset: IGetEnergyAsset) {
    console.log(asset);
    this.meterRef.set(asset.identifier.split('_')[2]);
    this.asset.set(asset);
  }

  filter() {
    this.grandTotals = [null, null, null, null, null, null, null, null, null, null, null, null];

    const filtered = this.assets()?.filter(asset => (this.gasToggle() && asset.assetType_id === AssetService.ASSET_TYPE_GAS_METER) || (this.electricToggle() && asset.assetType_id === AssetService.ASSET_TYPE_ELEC_METER));

    this.filteredAssets.set(filtered || []);
    filtered.forEach(asset => {
      if (asset.months) {
        const site = this.sites().find(s => s.id === asset.site_id);

        let total = 0;
        asset.months.forEach((v, i) => {
          site.totals[i] = (site.totals[i] || 0) + v;
          site['month_' + this.months[i].toLowerCase()] = site.totals[i];
          site['total'] += v;
          if (v !== null) {
            this.grandTotals[2 + i] = (this.grandTotals[2 + i] || 0) + v;
            total += v;

          }
          asset['month_' + this.months[i].toLowerCase()] = v;
        });
        asset['total'] = total;
        this.grandTotals[2 + 12] = (this.grandTotals[2 + 12] ?? 0) + total;
      }
    });

    this.dataTableAssets.set(new DataTable({ data: this.filteredAssets(), sortedColumns: null }, this.dataTableAssetsConfig));
    this.dataTableSites = new DataTable({ data: this.sites(), sortedColumns: null }, this.dataTableSitesConfig);
  }

  getAssets() {
    // const siteGrantTotals = [null, null, null, null, null, null, null, null, null, null, null, null];
    this.assets.set(null);
    this.assetService.getEnergyAssets(this.onYear()).then(assets => {
      assets.forEach(asset => asset.title = asset.display || asset.title);
      this.assets.set(assets.sort((a, b) => a.title > b.title ? 1 : -1));

      this.filter();
    });
  }

  localStorageGet() {
    const storageString = JSON.parse(localStorage.getItem('energy:assets:list') || "{}");
    this.onYear.set(storageString.year || new Date().getFullYear());
    this.tabIndex.set(storageString?.tabIndex || 0);
    if (storageString?.toggles) {
      this.electricToggle.set(storageString.toggles?.electric);
      this.gasToggle.set(storageString.toggles?.gas);
    }
  }

  localStorageSave() {
    localStorage.setItem('energy:assets:list', JSON.stringify({ year: this.onYear(), tabIndex: this.tabIndex(), toggles: { electric: this.electricToggle(), gas: this.gasToggle() } }));
  }

  goBack() {
    if (this.onYear() === 2022) {
      return;
    }
    this.onYear.set(this.onYear() - 1);
    this.localStorageSave();
    this.getAssets();
  }

  goForward() {
    if (this.onYear() === this.currentYear) {
      return;
    }
    this.onYear.set(this.onYear() + 1);
    this.localStorageSave();
    this.getAssets();
  }

  tabIndexSet(index: number) {
    this.tabIndex.set(index);
  }

  ngOnDestroy(): void {
    this.localStorageSave();
  }

}

class SiteWithTotals extends Site {
  totals: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  addressOneLine: string = '';
  month_jan: number = 0;
  month_feb: number = 0;
  month_mar: number = 0;
  month_apr: number = 0;
  month_may: number = 0;
  month_jun: number = 0;
  month_jul: number = 0;
  month_aug: number = 0;
  month_sep: number = 0;
  month_oct: number = 0;
  month_nov: number = 0;
  month_dec: number = 0;
  total: number = 0;

  constructor(data) {
    super(data);
    this.addressOneLine = this.address.addressTown;
  }
}
